'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import AdminCrudModal from '../../components/AdminCrudModal';
import {
  normalizeErrorMessage,
  normalizeFieldErrors,
  requestWithFallback,
  unpackCollection,
} from '../../services/adminCrudApi';
import { validateDokterForm, validateJadwalForm } from '../../services/adminMasterValidation';
import './Dokter.css';

const HARI_OPTIONS = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'];

const defaultForm = {
  nama: '', no_identitas: '', spesialisasi: '', no_lisensi: '',
  no_telepon: '', email: '', alamat: '',
  jam_praktek_mulai: '', jam_praktek_selesai: '',
  hari_libur: '', status: '', password: '',
};

const defaultJadwalForm = {
  dokter_id: '', hari: '', jam_mulai: '', jam_selesai: '', kapasitas: 10,
};

function StatusBadge({ active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        color: active ? '#047857' : '#B91C1C',
        backgroundColor: active ? '#D1FAE5' : '#FEE2E2',
        border: `1px solid ${active ? '#A7F3D0' : '#FECACA'}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
      title="Klik untuk ubah status"
    >
      <span style={{
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: active ? '#10B981' : '#EF4444',
        marginRight: '6px'
      }} />
      {active ? 'Aktif' : 'Tidak aktif'}
    </button>
  );
}

export default function Dokter() {
  const [activeTab, setActiveTab] = useState('dokter');

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ ...defaultForm });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [notice, setNotice] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  const [viewMode, setViewMode] = useState('hari');
  const [schedules, setSchedules] = useState([]);
  const [jadwalLoading, setJadwalLoading] = useState(false);
  const [showJadwalForm, setShowJadwalForm] = useState(false);
  const [jadwalForm, setJadwalForm] = useState(defaultJadwalForm);
  const [editingJadwalId, setEditingJadwalId] = useState(null);
  const [jadwalErrors, setJadwalErrors] = useState({});
  const [jadwalSubmitLoading, setJadwalSubmitLoading] = useState(false);
  const [deleteJadwalTarget, setDeleteJadwalTarget] = useState(null);
  const [jadwalSubmitError, setJadwalSubmitError] = useState('');

  const groupedByDay = schedules.reduce((acc, sch) => {
    if (!acc[sch.hari]) acc[sch.hari] = [];
    acc[sch.hari].push(sch);
    return acc;
  }, {});

  const groupedByDoctor = schedules.reduce((acc, sch) => {
    const name = sch.dokter?.nama || 'Dokter';
    if (!acc[name]) acc[name] = [];
    acc[name].push(sch);
    return acc;
  }, {});

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchDoctors = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (searchDebounced) params.search = searchDebounced;
    try {
      const res = await requestWithFallback([
        { method: 'get', url: '/admin/dokter', params },
      ]);
      setDoctors(unpackCollection(res.data));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [searchDebounced]);

  const fetchSchedules = useCallback(async () => {
    setJadwalLoading(true);
    try {
      const res = await requestWithFallback([
        { method: 'get', url: '/admin/jadwal', params: { per_page: 100 } },
      ]);

      const raw = res.data;
      let list = [];
      if (Array.isArray(raw)) {
        list = raw;
      } else if (Array.isArray(raw?.data)) {
        list = raw.data;
      }

      setSchedules(list);
    } catch (err) {
      console.error('fetchSchedules error:', err);
    } finally {
      setJadwalLoading(false);
    }
  }, []);

  useEffect(() => { fetchDoctors(); }, [fetchDoctors]);
  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setErrors({});
    const clientErrors = validateDokterForm(formData, { editing: Boolean(editingId) });
    if (Object.keys(clientErrors).length > 0) { setErrors(clientErrors); return; }
    setSubmitLoading(true);
    try {
      if (editingId) {
        const { nama, spesialisasi, no_telepon, hari_libur, status } = formData;
        await requestWithFallback([
          { method: 'put', url: `/admin/dokter/${editingId}`, data: { nama, spesialisasi, no_telepon, hari_libur, status: Number(status) } },
        ]);
      } else {
        await requestWithFallback([
          { method: 'post', url: '/admin/dokter', data: { ...formData, status: Number(formData.status) } },
        ]);
      }
      await fetchDoctors();
      cancelForm();
      setNotice(editingId ? 'Data dokter berhasil diperbarui.' : 'Data dokter berhasil ditambahkan.');
    } catch (err) {
      const fieldErrors = normalizeFieldErrors(err);
      if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      else setSubmitError(normalizeErrorMessage(err, 'Terjadi kesalahan, coba lagi.'));
    } finally {
      setSubmitLoading(false);
    }
  };

  const editDoctor = (doc) => {
    setEditingId(doc.id);
    setErrors({});
    setFormData({
      nama: doc.nama ?? '', no_identitas: doc.no_identitas ?? '',
      spesialisasi: doc.spesialisasi ?? '', no_lisensi: doc.no_lisensi ?? '',
      no_telepon: doc.no_telepon ?? '', email: doc.email ?? '',
      alamat: doc.alamat ?? '', jam_praktek_mulai: doc.jam_praktek_mulai ?? '',
      jam_praktek_selesai: doc.jam_praktek_selesai ?? '', hari_libur: doc.hari_libur ?? '',
      status: doc.status === true || doc.status === 1 ? '1' : '0',
    });
    setShowForm(true);
  };

  const toggleStatus = async (doc) => {
    const statusBaru = doc.status === true || doc.status === 1 ? 0 : 1;
    try {
      await requestWithFallback([
        { method: 'put', url: `/admin/dokter/${doc.id}`, data: { status: statusBaru } },
      ]);
      fetchDoctors();
      setNotice(`Status dokter ${doc.nama} berhasil diubah.`);
    } catch {
      setSubmitError('Gagal mengubah status dokter.');
    }
  };

  const deleteDoctor = async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    try {
      await requestWithFallback([
        { method: 'delete', url: `/admin/dokter/${deleteTarget.id}` },
      ]);
      await fetchDoctors();
      setDeleteTarget(null);
      setNotice('Data dokter berhasil dihapus.');
    } catch {
      setSubmitError('Gagal menghapus data dokter.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(defaultForm);
    setErrors({});
    setSubmitError('');
  };

  const handleJadwalChange = (e) => {
    const { name, value } = e.target;
    setJadwalForm((prev) => ({ ...prev, [name]: value }));
    setJadwalErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleJadwalSubmit = async (e) => {
    e.preventDefault();
    setJadwalSubmitError('');
    setJadwalErrors({});
    const clientErrors = validateJadwalForm(jadwalForm);
    if (Object.keys(clientErrors).length > 0) { setJadwalErrors(clientErrors); return; }
    const payload = { ...jadwalForm, dokter_id: Number(jadwalForm.dokter_id), kapasitas: Number(jadwalForm.kapasitas) };
    setJadwalSubmitLoading(true);
    try {
      if (editingJadwalId) {
        await requestWithFallback([
          { method: 'put', url: `/admin/jadwal/${editingJadwalId}`, data: payload },
        ]);
      } else {
        await requestWithFallback([
          { method: 'post', url: '/admin/jadwal', data: payload },
        ]);
      }
      await fetchSchedules();
      cancelJadwalForm();
      setNotice(editingJadwalId ? 'Jadwal berhasil diperbarui.' : 'Jadwal berhasil ditambahkan.');
    } catch (err) {
      const fieldErrors = normalizeFieldErrors(err);
      if (Object.keys(fieldErrors).length > 0) setJadwalErrors(fieldErrors);
      else setJadwalSubmitError(normalizeErrorMessage(err, 'Gagal menyimpan jadwal.'));
    } finally {
      setJadwalSubmitLoading(false);
    }
  };

  const cancelJadwalForm = () => {
    setShowJadwalForm(false);
    setEditingJadwalId(null);
    setJadwalForm(defaultJadwalForm);
    setJadwalErrors({});
    setJadwalSubmitError('');
  };

  const deleteJadwal = async () => {
    if (!deleteJadwalTarget?.id) return;
    try {
      await requestWithFallback([
        { method: 'delete', url: `/admin/jadwal/${deleteJadwalTarget.id}` },
      ]);
      await fetchSchedules();
      setDeleteJadwalTarget(null);
      setNotice('Jadwal berhasil dihapus.');
    } catch {
      setJadwalSubmitError('Gagal menghapus jadwal.');
    }
  };

  const renderError = (field) =>
    errors[field] ? <small style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors[field][0]}</small> : null;

  const renderJadwalError = (field) =>
    jadwalErrors[field] ? <small style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{jadwalErrors[field][0] ?? jadwalErrors[field]}</small> : null;

  const isAktif = (status) => status === 1 || status === true;

  return (
    <AdminLayout title="">

      {/* ── HEADER HALAMAN ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#111827', margin: 0 }}>Manajemen Dokter</h1>
          <p style={{ color: '#6B7280', margin: '4px 0 0', fontSize: '14px' }}>Kelola data dokter dan jadwal praktik klinik</p>
        </div>
      </div>

      {/* ── NOTIFIKASI ── */}
      {notice && (
        <div style={{ padding: '12px 16px', backgroundColor: '#D1FAE5', color: '#065F46', borderRadius: '10px', fontSize: '14px', marginBottom: '16px', border: '1px solid #A7F3D0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{notice}</span>
          <button onClick={() => setNotice('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065F46', fontWeight: 'bold' }}>✕</button>
        </div>
      )}

      {/* ── TABS ── */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #E5E7EB', marginBottom: '20px' }}>
        {[
          { key: 'dokter', label: 'Data Dokter' },
          { key: 'jadwal', label: 'Jadwal Kerja' },
        ].map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 18px',
              fontSize: '14px',
              fontWeight: '600',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #2563EB' : '2px solid transparent',
              color: activeTab === tab.key ? '#2563EB' : '#6B7280',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== TAB DOKTER ===== */}
      {activeTab === 'dokter' && (
        <>
          <div style={{ background: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '16px 20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ flex: '1', minWidth: '240px', maxWidth: '400px' }}>
              <input
                type="search"
                placeholder="Cari nama atau spesialisasi..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                autoComplete="off"
                style={{ width: '100%', padding: '9px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '13px', outline: 'none', backgroundColor: '#F9FAFB' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', backgroundColor: '#F3F4F6', padding: '6px 12px', borderRadius: '20px' }}>
                Total: {doctors.length} Dokter
              </span>
              <button
                onClick={() => { setShowForm(true); setEditingId(null); setFormData(defaultForm); }}
                style={{ padding: '9px 16px', backgroundColor: '#2563EB', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
              >
                + Tambah Dokter
              </button>
            </div>
          </div>

          <div style={{ background: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '48px 20px', color: '#6B7280', fontSize: '14px' }}>⏳ Memuat data dokter...</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                      <th style={thStyle}>Nama</th>
                      <th style={thStyle}>Spesialisasi</th>
                      <th style={thStyle}>No. Telepon</th>
                      <th style={thStyle}>Jam Praktek</th>
                      <th style={thStyle}>Hari Libur</th>
                      <th style={thStyle}>Status</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctors.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '48px 20px', color: '#9CA3AF', fontSize: '14px' }}>
                          Belum ada data dokter
                        </td>
                      </tr>
                    ) : (
                      doctors.map((doc) => (
                        <tr key={doc.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                          <td style={tdStyle}>
                            <div style={{ fontWeight: '600', color: '#111827' }}>{doc.nama}</div>
                            <div style={{ fontSize: '12px', color: '#6B7280' }}>{doc.email}</div>
                          </td>
                          <td style={tdStyle}><span style={{ backgroundColor: '#EFF6FF', color: '#1E40AF', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>{doc.spesialisasi}</span></td>
                          <td style={tdStyle}>{doc.no_telepon}</td>
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap', color: '#4B5563' }}>
                            {doc.jam_praktek_mulai} – {doc.jam_praktek_selesai}
                          </td>
                          <td style={tdStyle}>{doc.hari_libur ?? '–'}</td>
                          <td style={tdStyle}>
                            <StatusBadge active={isAktif(doc.status)} onClick={() => toggleStatus(doc)} />
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', gap: '6px', justifyContent: 'flex-end' }}>
                              <button onClick={() => editDoctor(doc)} style={btnActionStyle('#2563EB')}>Edit</button>
                              <button onClick={() => setDeleteTarget(doc)} style={btnActionStyle('#EF4444')}>Hapus</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ===== TAB JADWAL ===== */}
      {activeTab === 'jadwal' && (
        <>
          <div style={{ background: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '16px 20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ display: 'inline-flex', background: '#F3F4F6', padding: '3px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                <button
                  onClick={() => setViewMode('hari')}
                  style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600', border: 'none', borderRadius: '6px', cursor: 'pointer', backgroundColor: viewMode === 'hari' ? '#FFFFFF' : 'transparent', color: viewMode === 'hari' ? '#111827' : '#6B7280', boxShadow: viewMode === 'hari' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none' }}
                >
                  Per Hari
                </button>
                <button
                  onClick={() => setViewMode('dokter')}
                  style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600', border: 'none', borderRadius: '6px', cursor: 'pointer', backgroundColor: viewMode === 'dokter' ? '#FFFFFF' : 'transparent', color: viewMode === 'dokter' ? '#111827' : '#6B7280', boxShadow: viewMode === 'dokter' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none' }}
                >
                  Per Dokter
                </button>
              </div>
              <span style={{ fontSize: '12px', color: '#6B7280', italic: 'true' }}>*Jadwal diatur oleh dokter (hanya lihat)</span>
            </div>
            <div>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', backgroundColor: '#F3F4F6', padding: '6px 12px', borderRadius: '20px' }}>
                Total: {schedules.length} Jadwal
              </span>
            </div>
          </div>

          {jadwalLoading ? (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: '#6B7280', fontSize: '14px' }}>⏳ Memuat jadwal...</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>

              {/* PER HARI */}
              {viewMode === 'hari' && HARI_OPTIONS.map(day => {
                const list = groupedByDay[day];
                if (!list) return null;
                return (
                  <div key={day} style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #F3F4F6' }}>
                      <span style={{ fontWeight: '700', color: '#111827', fontSize: '15px' }}>{day}</span>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#2563EB', backgroundColor: '#EFF6FF', padding: '2px 8px', borderRadius: '12px' }}>{list.length} jadwal</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {list.map((sch) => (
                        <div key={sch.id} style={{ padding: '10px 12px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #F3F4F6' }}>
                          <div style={{ fontWeight: '600', fontSize: '13px', color: '#1F2937' }}>{sch.dokter?.nama ?? '–'}</div>
                          <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                            🕒 {sch.jam_check_in_hari_ini?.substring(0, 5) || sch.jam_mulai?.substring(0, 5)} – {sch.jam_check_out_hari_ini?.substring(0, 5) || sch.jam_selesai?.substring(0, 5)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* PER DOKTER */}
              {viewMode === 'dokter' && Object.keys(groupedByDoctor).map(doc => (
                <div key={doc} style={{ background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #F3F4F6' }}>
                    <span style={{ fontWeight: '700', color: '#111827', fontSize: '15px' }}>{doc}</span>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#2563EB', backgroundColor: '#EFF6FF', padding: '2px 8px', borderRadius: '12px' }}>{groupedByDoctor[doc].length} jadwal</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {groupedByDoctor[doc].map((sch) => (
                      <div key={sch.id} style={{ padding: '10px 12px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>{sch.hari}</span>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>
                          🕒 {sch.jam_check_in_hari_ini?.substring(0, 5) || sch.jam_mulai?.substring(0, 5)} – {sch.jam_check_out_hari_ini?.substring(0, 5) || sch.jam_selesai?.substring(0, 5)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

            </div>
          )}
        </>
      )}

      {/* ── MODAL FORM DOKTER ── */}
      <AdminCrudModal
        open={showForm}
        title={editingId ? 'Edit Dokter' : 'Tambah Dokter Baru'}
        onClose={cancelForm}
      >
        <div style={{ padding: '4px' }}>
          {submitError && (
            <div style={{ padding: '10px 14px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', border: '1px solid #FECACA' }}>
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Nama lengkap <span style={{ color: '#EF4444' }}>*</span></label>
                <input type="text" name="nama" value={formData.nama} onChange={handleChange} required maxLength={255} placeholder="dr. Budi Santoso" style={inputStyle} />
                {renderError('nama')}
              </div>
              <div>
                <label style={labelStyle}>Spesialisasi <span style={{ color: '#EF4444' }}>*</span></label>
                <input type="text" name="spesialisasi" value={formData.spesialisasi} onChange={handleChange} required maxLength={100} placeholder="Umum / Gigi..." style={inputStyle} />
                {renderError('spesialisasi')}
              </div>
              <div>
                <label style={labelStyle}>No. telepon <span style={{ color: '#EF4444' }}>*</span></label>
                <input type="text" name="no_telepon" value={formData.no_telepon} onChange={handleChange} required maxLength={20} placeholder="08xx..." style={inputStyle} />
                {renderError('no_telepon')}
              </div>
              <div>
                <label style={labelStyle}>Hari libur</label>
                <select name="hari_libur" value={formData.hari_libur} onChange={handleChange} style={inputStyle}>
                  <option value="">Tidak ada</option>
                  {HARI_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                {renderError('hari_libur')}
              </div>
              <div>
                <label style={labelStyle}>Status <span style={{ color: '#EF4444' }}>*</span></label>
                <select name="status" value={formData.status} onChange={handleChange} required style={inputStyle}>
                  <option value="">Pilih status</option>
                  <option value="1">Aktif</option>
                  <option value="0">Tidak aktif</option>
                </select>
                {renderError('status')}
              </div>
              <div>
                <label style={labelStyle}>Password Login</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password ?? ''}
                  onChange={handleChange}
                  maxLength={50}
                  placeholder="Default: password123"
                  style={inputStyle}
                />
                {renderError('password')}
              </div>
            </div>

            {!editingId && (
              <>
                <div style={{ margin: '18px 0 12px', borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#374151' }}>Informasi Identitas & Jadwal Praktik</p>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={labelStyle}>No. identitas <span style={{ color: '#EF4444' }}>*</span></label>
                    <input type="text" name="no_identitas" value={formData.no_identitas} onChange={handleChange} required maxLength={50} style={inputStyle} />
                    {renderError('no_identitas')}
                  </div>
                  <div>
                    <label style={labelStyle}>No. lisensi <span style={{ color: '#EF4444' }}>*</span></label>
                    <input type="text" name="no_lisensi" value={formData.no_lisensi} onChange={handleChange} required maxLength={50} style={inputStyle} />
                    {renderError('no_lisensi')}
                  </div>
                  <div>
                    <label style={labelStyle}>Email <span style={{ color: '#EF4444' }}>*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required maxLength={255} style={inputStyle} />
                    {renderError('email')}
                  </div>
                  <div>
                    <label style={labelStyle}>Alamat <span style={{ color: '#EF4444' }}>*</span></label>
                    <input type="text" name="alamat" value={formData.alamat} onChange={handleChange} required maxLength={500} style={inputStyle} />
                    {renderError('alamat')}
                  </div>
                  <div>
                    <label style={labelStyle}>Jam praktek mulai <span style={{ color: '#EF4444' }}>*</span></label>
                    <input type="time" name="jam_praktek_mulai" value={formData.jam_praktek_mulai} onChange={handleChange} required style={inputStyle} />
                    {renderError('jam_praktek_mulai')}
                  </div>
                  <div>
                    <label style={labelStyle}>Jam praktek selesai <span style={{ color: '#EF4444' }}>*</span></label>
                    <input type="time" name="jam_praktek_selesai" value={formData.jam_praktek_selesai} onChange={handleChange} required style={inputStyle} />
                    {renderError('jam_praktek_selesai')}
                  </div>
                </div>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <button type="button" onClick={cancelForm} style={btnSecondaryStyle}>Batal</button>
              <button type="submit" disabled={submitLoading} style={btnPrimaryStyle}>
                {submitLoading ? 'Menyimpan...' : editingId ? 'Perbarui' : 'Simpan'}
              </button>
            </div>
          </form>
        </div>
      </AdminCrudModal>

      {/* ── MODAL FORM JADWAL ── */}
      <AdminCrudModal
        open={showJadwalForm}
        title={editingJadwalId ? 'Edit Jadwal' : 'Tambah Jadwal'}
        onClose={cancelJadwalForm}
      >
        <div style={{ padding: '4px' }}>
          {jadwalSubmitError && (
            <div style={{ padding: '10px 14px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', border: '1px solid #FECACA' }}>
              {jadwalSubmitError}
            </div>
          )}

          <form onSubmit={handleJadwalSubmit}>
            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Dokter <span style={{ color: '#EF4444' }}>*</span></label>
              <select name="dokter_id" value={jadwalForm.dokter_id} onChange={handleJadwalChange} required style={inputStyle}>
                <option value="">Pilih dokter</option>
                {doctors.map(d => <option key={d.id} value={d.id}>{d.nama}</option>)}
              </select>
              {renderJadwalError('dokter_id')}
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={labelStyle}>Hari <span style={{ color: '#EF4444' }}>*</span></label>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                {HARI_OPTIONS.map(day => {
                  const selected = jadwalForm.hari === day;
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        setJadwalForm(f => ({ ...f, hari: day }));
                        setJadwalErrors(p => ({ ...p, hari: null }));
                      }}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: selected ? '1px solid #2563EB' : '1px solid #D1D5DB',
                        backgroundColor: selected ? '#EFF6FF' : '#FFFFFF',
                        color: selected ? '#2563EB' : '#374151',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {day.substring(0, 3)}
                    </button>
                  );
                })}
              </div>
              {renderJadwalError('hari')}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
              <div>
                <label style={labelStyle}>Jam mulai <span style={{ color: '#EF4444' }}>*</span></label>
                <input type="time" name="jam_mulai" value={jadwalForm.jam_mulai} onChange={handleJadwalChange} required style={inputStyle} />
                {renderJadwalError('jam_mulai')}
              </div>
              <div>
                <label style={labelStyle}>Jam selesai <span style={{ color: '#EF4444' }}>*</span></label>
                <input type="time" name="jam_selesai" value={jadwalForm.jam_selesai} onChange={handleJadwalChange} required style={inputStyle} />
                {renderJadwalError('jam_selesai')}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <button type="button" onClick={cancelJadwalForm} disabled={jadwalSubmitLoading} style={btnSecondaryStyle}>
                Batal
              </button>
              <button type="submit" disabled={jadwalSubmitLoading} style={btnPrimaryStyle}>
                {jadwalSubmitLoading ? 'Menyimpan...' : editingJadwalId ? 'Update Jadwal' : 'Simpan Jadwal'}
              </button>
            </div>
          </form>
        </div>
      </AdminCrudModal>

      {/* ── MODAL HAPUS DOKTER ── */}
      <AdminCrudModal
        open={Boolean(deleteTarget)}
        title="Hapus Dokter"
        onClose={() => setDeleteTarget(null)}
        size="sm"
      >
        <div style={{ padding: '4px' }}>
          <p style={{ margin: '0 0 20px', color: '#374151', fontSize: '14px' }}>
            Hapus data dokter <strong>{deleteTarget?.nama}</strong>? Tindakan ini tidak dapat dibatalkan.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleteLoading} style={btnSecondaryStyle}>
              Batal
            </button>
            <button type="button" onClick={deleteDoctor} disabled={deleteLoading} style={btnDangerStyle}>
              {deleteLoading ? 'Menghapus...' : 'Ya, Hapus'}
            </button>
          </div>
        </div>
      </AdminCrudModal>

      {/* ── MODAL HAPUS JADWAL ── */}
      <AdminCrudModal
        open={Boolean(deleteJadwalTarget)}
        title="Hapus Jadwal"
        onClose={() => setDeleteJadwalTarget(null)}
        size="sm"
      >
        <div style={{ padding: '4px' }}>
          <p style={{ margin: '0 0 20px', color: '#374151', fontSize: '14px' }}>
            Hapus jadwal <strong>{deleteJadwalTarget?.dokter?.nama}</strong> hari <strong>{deleteJadwalTarget?.hari}</strong>?
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button type="button" onClick={() => setDeleteJadwalTarget(null)} style={btnSecondaryStyle}>Batal</button>
            <button type="button" onClick={deleteJadwal} style={btnDangerStyle}>Ya, Hapus</button>
          </div>
        </div>
      </AdminCrudModal>

    </AdminLayout>
  );
}

// ===================== STYLES =====================
const thStyle = {
  padding: '14px 16px',
  fontWeight: '700',
  fontSize: '12px',
  color: '#4B5563',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const tdStyle = {
  padding: '14px 16px',
  verticalAlign: 'middle',
  fontSize: '13px',
  color: '#374151'
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '12px',
  fontWeight: '600',
  color: '#374151'
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1px solid #D1D5DB',
  fontSize: '13px',
  outline: 'none',
  backgroundColor: '#F9FAFB',
  boxSizing: 'border-box'
};

const btnActionStyle = (bgColor) => ({
  padding: '6px 12px',
  backgroundColor: bgColor,
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: '600'
});

const btnPrimaryStyle = {
  padding: '9px 16px',
  backgroundColor: '#2563EB',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600'
};

const btnSecondaryStyle = {
  padding: '9px 16px',
  backgroundColor: '#FFFFFF',
  color: '#374151',
  border: '1px solid #D1D5DB',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600'
};

const btnDangerStyle = {
  padding: '9px 16px',
  backgroundColor: '#EF4444',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600'
};